import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Edit, Send, CheckCircle, XCircle, RotateCcw, Pause, Play, Trash2, Package, AlertTriangle, Plus, FilePlus } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

const pretty = s => s ? String(s).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "";
const STATUS_COLORS = { draft: "bg-zinc-100 text-zinc-700", submitted: "bg-blue-100 text-blue-700", sent_back: "bg-amber-100 text-amber-700", live: "bg-emerald-100 text-emerald-700", suspended: "bg-red-100 text-red-700", rejected: "bg-red-50 text-red-600" };

const CHANGE_FIELDS = [
  { path: "display_name", label: "Display Name", perm: null },
  { path: "identity.description", label: "Description", perm: null },
  { path: "identity.website", label: "Website", perm: null },
  { path: "identity.country_of_origin", label: "Country of Origin", perm: null },
  { path: "company.legal_name", label: "Legal Name", perm: "brands.view_kyc" },
  { path: "company.gst_number", label: "GST Number", perm: "brands.view_kyc" },
  { path: "company.pan_number", label: "PAN", perm: "brands.view_kyc" },
  { path: "company.fssai_number", label: "FSSAI Number", perm: "brands.view_kyc" },
  { path: "company.fssai_expiry", label: "FSSAI Expiry", perm: "brands.view_kyc" },
  { path: "bank.bank_account_number", label: "Bank Account Number", perm: "brands.view_kyc" },
  { path: "bank.bank_ifsc", label: "Bank IFSC", perm: "brands.view_kyc" },
  { path: "terms.payment_terms_days", label: "Payment Terms (days)", perm: "brands.view_terms" },
  { path: "terms.mdf_percent", label: "MDF %", perm: "brands.view_terms" },
];

export default function BrandDetailPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getItems } = useConfig();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
  const can = (p) => isAdmin || perms.includes(p);

  const [brand, setBrand] = useState(null);
  const [tab, setTab] = useState("overview");
  const [timeline, setTimeline] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  const [listings, setListings] = useState([]);
  const [dialogType, setDialogType] = useState(null);
  const [reasonCode, setReasonCode] = useState("");
  const [dialogNote, setDialogNote] = useState("");
  const [crOpen, setCrOpen] = useState(false);
  const [crValues, setCrValues] = useState({});
  const [crReason, setCrReason] = useState("");
  const [restrictEdit, setRestrictEdit] = useState({ blocked_states: "", blocked_pincodes: "", blocked_prefixes: "", reason: "" });
  const [contactsEdit, setContactsEdit] = useState(null);

  const load = useCallback(() => {
    api.get(`/brands/${brandId}`).then(r => setBrand(r.data)).catch(() => toast.error("Failed to load"));
    api.get(`/brands/${brandId}/timeline`).then(r => { setTimeline(r.data?.entries || []); setChangeHistory(r.data?.change_requests || []); }).catch(() => {});
    api.get(`/brands/${brandId}/listings`).then(r => setListings(r.data)).catch(() => {});
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  if (!brand) return <p className="text-xs text-zinc-400 p-4">Loading...</p>;

  const lc = brand.lifecycle_status;
  const Row = ({ label, value }) => value ? <div className="flex"><span className="text-zinc-500 w-36 shrink-0">{label}</span><span className="text-zinc-900">{value}</span></div> : null;

  const reasonGroup = dialogType === "reject" ? "brand_reject_reasons" : dialogType === "send-back" ? "brand_send_back_reasons" : "brand_suspend_reasons";
  const reasonOptions = (getItems(reasonGroup) || []).map(i => ({ key: i.key, label: i.value || i.key }));

  const doAction = async (action, body = {}) => {
    try {
      const r = await api.post(`/brands/${brandId}/${action}`, body);
      toast.success(r.data.message);
      setDialogType(null); setReasonCode(""); setDialogNote("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.errors ? d.errors.join(", ") : `${action} failed`));
    }
  };

  const submitChangeRequest = async () => {
    const changes = {};
    Object.entries(crValues).forEach(([k, v]) => { if (v !== "" && v !== undefined) changes[k] = v; });
    if (restrictEdit.touched) {
      changes["restrictions"] = {
        blocked_states: restrictEdit.blocked_states.split(/[,\n]+/).map(s => s.trim()).filter(Boolean),
        blocked_pincodes: restrictEdit.blocked_pincodes.split(/[,\s\n]+/).map(s => s.trim()).filter(Boolean),
        blocked_prefixes: restrictEdit.blocked_prefixes.split(/[,\s\n]+/).map(s => s.trim()).filter(Boolean),
        reason: restrictEdit.reason,
      };
    }
    if (Object.keys(changes).length === 0) { toast.error("Change at least one field"); return; }
    if (!crReason.trim()) { toast.error("A reason is required"); return; }
    try {
      await api.post(`/brands/${brandId}/change-requests`, { changes, reason: crReason });
      toast.success("Change request sent for approval");
      setCrOpen(false); setCrValues({}); setCrReason(""); setRestrictEdit({ blocked_states: "", blocked_pincodes: "", blocked_prefixes: "", reason: "" });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Change request failed");
    }
  };

  const saveContacts = async () => {
    try {
      await api.put(`/brands/${brandId}`, { contacts: contactsEdit });
      toast.success("Contacts saved");
      setContactsEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const tabs = ["overview"];
  if (can("brands.view_kyc")) tabs.push("company", "bank", "documents");
  if (can("brands.view_contacts")) tabs.push("contacts");
  if (can("brands.view_terms")) tabs.push("terms");
  tabs.push("restrictions", "products", "timeline");

  const id = brand.identity || {};
  const co = brand.company || {};
  const bk = brand.bank || {};
  const contactRoles = (getItems("brand_contact_roles") || []).map(i => i.value || i.key);
  const sentCount = listings.filter(l => l.listing_status === "sent_to_catalogue").length;

  return (
    <div className="space-y-4" data-testid="brand-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-zinc-900">{brand.display_name || brand.name}</h1>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[lc] || ""}`} data-testid="brand-lifecycle-badge">{pretty(lc)}</span>
          {brand.legacy_unreviewed && <Badge variant="outline" className="border-amber-300 text-amber-600 text-[10px]">Needs review</Badge>}
          <span className="text-xs text-zinc-400">{pretty(id.owner_type || brand.owner_type || "")}</span>
          <span className="text-xs text-zinc-400" data-testid="brand-counts">{brand.listing_count || 0} listings · {sentCount} in catalogue</span>
        </div>
        <div className="flex gap-2">
          {(lc === "draft" || lc === "sent_back") && can("brands.edit") && <Button size="sm" variant="outline" onClick={() => navigate(`/brands/${brandId}/edit`)} data-testid="edit-brand-btn"><Edit className="w-3.5 h-3.5 mr-1" /> Edit</Button>}
          {(lc === "draft" || lc === "sent_back") && can("brands.submit") && <Button size="sm" onClick={() => doAction("submit")} data-testid="submit-brand-btn"><Send className="w-3.5 h-3.5 mr-1" /> Submit</Button>}
          {lc === "live" && can("brands.edit") && !brand.pending_change_request && <Button size="sm" variant="outline" onClick={() => setCrOpen(true)} data-testid="request-change-btn"><FilePlus className="w-3.5 h-3.5 mr-1" /> Request Change</Button>}
          {lc === "submitted" && can("brands.approve") && (
            <>
              <Button size="sm" className="bg-emerald-600" onClick={() => doAction("approve")} data-testid="approve-brand-btn"><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button size="sm" variant="destructive" onClick={() => setDialogType("reject")} data-testid="reject-brand-btn"><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              <Button size="sm" variant="outline" onClick={() => setDialogType("send-back")} data-testid="send-back-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Send Back</Button>
            </>
          )}
          {lc === "live" && can("brands.suspend") && <Button size="sm" variant="destructive" onClick={() => setDialogType("suspend")} data-testid="suspend-brand-btn"><Pause className="w-3.5 h-3.5 mr-1" /> Suspend</Button>}
          {lc === "suspended" && can("brands.suspend") && <Button size="sm" className="bg-emerald-600" onClick={() => doAction("reactivate")} data-testid="reactivate-brand-btn"><Play className="w-3.5 h-3.5 mr-1" /> Reactivate</Button>}
          {lc === "rejected" && can("brands.edit") && <Button size="sm" variant="outline" onClick={() => doAction("reopen")} data-testid="reopen-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen as Draft</Button>}
          {lc === "draft" && can("brands.edit") && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => doAction("discard")} data-testid="discard-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Discard</Button>}
        </div>
      </div>

      {brand.warnings?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="warnings-panel">
          <p className="text-xs font-medium text-amber-700 mb-1"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Warnings (do not block approval)</p>
          {brand.warnings.map((w, i) => <p key={i} className="text-xs text-amber-600">- {w}</p>)}
        </div>
      )}

      {brand.pending_change_request && (
        <div className="border border-blue-200 bg-blue-50 rounded-md p-3 text-xs text-blue-700" data-testid="pending-change-banner">
          A change is waiting for approval.
          <div className="mt-1 space-y-0.5">
            {Object.entries(brand.pending_change_request.changes || {}).map(([k, v]) => (
              <p key={k} data-testid={`pending-change-${k}`}>{k}: <span className="line-through">{String(v.old ?? "")}</span> → <span className="font-medium">{String(v.new ?? "")}</span></p>
            ))}
          </div>
        </div>
      )}

      {(lc === "submitted" || lc === "rejected") && brand.workflow?.rejection_reason && (
        <div className="border border-red-200 bg-red-50 rounded-md p-3 text-xs text-red-700">Rejected: {brand.workflow.rejection_reason}</div>
      )}
      {lc === "suspended" && brand.workflow?.suspend_reason && (
        <div className="border border-red-200 bg-red-50 rounded-md p-3 text-xs text-red-700" data-testid="suspend-reason-banner">Suspended: {brand.workflow.suspend_reason}</div>
      )}

      {/* Reason dialog */}
      {dialogType && (
        <div className="border rounded-md p-4 bg-white shadow-md" data-testid="action-dialog">
          <p className="text-sm font-medium mb-2">{dialogType === "reject" ? "Rejection Reason" : dialogType === "send-back" ? "Send-back Reason" : "Suspension Reason"} <span className="text-red-500">*</span></p>
          <select className="w-full h-8 text-xs border rounded-md px-2 mb-2" value={reasonCode} onChange={e => setReasonCode(e.target.value)} data-testid="reason-select">
            <option value="">Choose a reason...</option>
            {reasonOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <Input className="h-8 text-xs mb-2" value={dialogNote} onChange={e => setDialogNote(e.target.value)} placeholder="Note (required when reason is Other)" data-testid="dialog-input" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => doAction(dialogType, { reason_code: reasonCode, note: dialogNote })} data-testid="dialog-confirm">Confirm</Button>
            <Button size="sm" variant="outline" onClick={() => { setDialogType(null); setReasonCode(""); setDialogNote(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Change request form */}
      {crOpen && (
        <div className="border rounded-md p-4 bg-white shadow-md space-y-2" data-testid="change-request-form">
          <p className="text-sm font-medium">Request a change</p>
          <p className="text-xs text-zinc-500">Only fill in what should change. An admin has to approve it.</p>
          <div className="grid grid-cols-2 gap-2">
            {CHANGE_FIELDS.filter(f => !f.perm || can(f.perm)).map(f => (
              <div key={f.path}>
                <Label className="text-xs text-zinc-500">{f.label}</Label>
                <Input className="h-8 text-xs mt-0.5" value={crValues[f.path] ?? ""} onChange={e => setCrValues(v => ({ ...v, [f.path]: e.target.value }))} data-testid={`cr-${f.path.replace(/\./g, "-")}`} />
              </div>
            ))}
          </div>
          <div className="border-t pt-2">
            <Label className="text-xs text-zinc-500">Restrictions (where NOT to sell)</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input className="h-8 text-xs" placeholder="Blocked states, comma-separated" value={restrictEdit.blocked_states} onChange={e => setRestrictEdit(r => ({ ...r, blocked_states: e.target.value, touched: true }))} data-testid="cr-blocked-states" />
              <Input className="h-8 text-xs" placeholder="Blocked pincodes" value={restrictEdit.blocked_pincodes} onChange={e => setRestrictEdit(r => ({ ...r, blocked_pincodes: e.target.value, touched: true }))} data-testid="cr-blocked-pincodes" />
              <Input className="h-8 text-xs" placeholder="Blocked 3-digit areas" value={restrictEdit.blocked_prefixes} onChange={e => setRestrictEdit(r => ({ ...r, blocked_prefixes: e.target.value, touched: true }))} data-testid="cr-blocked-prefixes" />
              <Input className="h-8 text-xs" placeholder="Restriction reason" value={restrictEdit.reason} onChange={e => setRestrictEdit(r => ({ ...r, reason: e.target.value, touched: true }))} data-testid="cr-restriction-reason" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Why is this change needed? <span className="text-red-500">*</span></Label>
            <Input className="h-8 text-xs mt-0.5" value={crReason} onChange={e => setCrReason(e.target.value)} data-testid="cr-reason" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submitChangeRequest} data-testid="cr-submit">Send for Approval</Button>
            <Button size="sm" variant="outline" onClick={() => setCrOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b">
        {tabs.map(t => <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs font-medium border-b-2 transition ${tab === t ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`} data-testid={`tab-${t}`}>{pretty(t)}</button>)}
      </div>

      <div className="text-xs space-y-2">
        {tab === "overview" && (
          <div className="space-y-1.5">
            <Row label="Display Name" value={brand.display_name} />
            <Row label="Owner Type" value={pretty(id.owner_type || brand.owner_type || "")} />
            <Row label="Description" value={id.description} />
            <Row label="Website" value={id.website} />
            <Row label="Country of Origin" value={id.country_of_origin} />
            <Row label="Year Started" value={id.year_started} />
            <Row label="Already Selling In" value={(id.already_selling_in || []).join(", ")} />
            <Row label="Completeness" value={`${brand.completeness || 0}%`} />
            <Row label="Products" value={`${brand.listing_count || 0} listed`} />
          </div>
        )}
        {tab === "company" && (
          <div className="space-y-1.5">
            <Row label="Legal Name" value={co.legal_name} />
            <Row label="Entity Type" value={co.entity_type} />
            <Row label="GST Number" value={co.gst_number} />
            <Row label="PAN" value={co.pan_number} />
            <Row label="FSSAI Number" value={co.fssai_number} />
            <Row label="FSSAI Expiry" value={co.fssai_expiry} />
            <Row label="Trademark No." value={co.trademark_number} />
            <Row label="Registered Address" value={co.registered_address} />
            <Row label="Manufacturer" value={co.manufacturer_name} />
            <Row label="Marketer" value={co.marketer_name} />
          </div>
        )}
        {tab === "contacts" && (
          <div data-testid="contacts-tab">
            {!contactsEdit && (brand.contacts || []).map((c, i) => (
              <div key={i} className="border rounded-sm p-2 mb-2 flex items-center gap-4">
                <span className="font-medium">{c.name}</span>
                <span className="text-zinc-500">{c.role}</span>
                <span>{c.phone}</span>
                <span className="text-zinc-500">{c.email}</span>
                {c.is_primary && <Badge className="text-[9px]">Primary</Badge>}
              </div>
            ))}
            {!contactsEdit && can("brands.view_contacts") && can("brands.edit") && (lc === "live" || lc === "suspended") && (
              <Button size="sm" variant="outline" onClick={() => setContactsEdit((brand.contacts || []).map(c => ({ ...c })))} data-testid="edit-contacts-btn"><Edit className="w-3 h-3 mr-1" /> Edit Contacts</Button>
            )}
            {contactsEdit && (
              <div className="space-y-2" data-testid="contacts-editor">
                {contactsEdit.map((c, i) => (
                  <div key={i} className="flex gap-2 items-end border-b pb-2">
                    <div className="flex-1"><Label className="text-xs">Name</Label><Input className="h-8 text-xs" value={c.name || ""} onChange={e => { const a = [...contactsEdit]; a[i] = { ...a[i], name: e.target.value }; setContactsEdit(a); }} data-testid={`contact-edit-name-${i}`} /></div>
                    <div className="w-32"><Label className="text-xs">Role</Label>
                      <select className="w-full h-8 text-xs border rounded-md px-1" value={c.role || ""} onChange={e => { const a = [...contactsEdit]; a[i] = { ...a[i], role: e.target.value }; setContactsEdit(a); }}>
                        <option value="">Select...</option>
                        {contactRoles.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="flex-1"><Label className="text-xs">Phone</Label><Input className="h-8 text-xs" value={c.phone || ""} onChange={e => { const a = [...contactsEdit]; a[i] = { ...a[i], phone: e.target.value }; setContactsEdit(a); }} /></div>
                    <div className="flex-1"><Label className="text-xs">Email</Label><Input className="h-8 text-xs" value={c.email || ""} onChange={e => { const a = [...contactsEdit]; a[i] = { ...a[i], email: e.target.value }; setContactsEdit(a); }} /></div>
                    <label className="flex items-center gap-1 text-xs"><input type="radio" name="primary_edit" checked={!!c.is_primary} onChange={() => setContactsEdit(contactsEdit.map((x, j) => ({ ...x, is_primary: j === i })))} /> Primary</label>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setContactsEdit([...contactsEdit, { name: "", role: "", phone: "", email: "", is_primary: false }])}><Plus className="w-3 h-3 mr-1" /> Add</Button>
                  <Button size="sm" onClick={saveContacts} data-testid="save-contacts-btn">Save Contacts</Button>
                  <Button size="sm" variant="outline" onClick={() => setContactsEdit(null)}>Cancel</Button>
                </div>
              </div>
            )}
            {brand.crm_party_id && <a href={`/crm/${brand.crm_party_id}`} className="text-blue-600 underline text-xs mt-2 block">CRM Record</a>}
          </div>
        )}
        {tab === "bank" && (
          <div className="space-y-1.5">
            <Row label="Account Name" value={bk.bank_account_name} />
            <Row label="Account Number" value={bk.bank_account_number} />
            <Row label="IFSC" value={bk.bank_ifsc} />
            <Row label="Bank" value={bk.bank_name} />
            <Row label="Branch" value={bk.bank_branch} />
          </div>
        )}
        {tab === "terms" && (
          <div className="space-y-1.5">
            {brand.terms && Object.keys(brand.terms).length > 0 ? Object.entries(brand.terms).map(([k, v]) => v !== null && v !== undefined && v !== "" ? <Row key={k} label={pretty(k)} value={String(v)} /> : null) : <p className="text-zinc-400">No terms recorded</p>}
          </div>
        )}
        {tab === "restrictions" && (
          <div className="space-y-1.5" data-testid="restrictions-tab">
            <Row label="Blocked States" value={(brand.restrictions?.blocked_states || []).join(", ") || "None"} />
            <Row label="Blocked Pincodes" value={(brand.restrictions?.blocked_pincodes || []).join(", ") || "None"} />
            <Row label="Blocked Prefixes" value={(brand.restrictions?.blocked_prefixes || []).join(", ") || "None"} />
            <Row label="Reason" value={brand.restrictions?.reason || "—"} />
            {lc === "live" && <p className="text-zinc-500 italic pt-2">Restrictions on a live brand change through Request Change.</p>}
          </div>
        )}
        {tab === "documents" && (
          <div>
            {(brand.documents || []).filter(d => !d.is_deleted).map(d => (
              <div key={d.doc_id} className="border rounded-sm p-2 mb-1 flex items-center justify-between">
                <div><span className="font-medium">{d.doc_type}</span> — {d.file_name} ({Math.round((d.size || 0) / 1024)} KB)</div>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/storage/${d.file_path}`} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">View</a>
              </div>
            ))}
            {(brand.documents || []).filter(d => !d.is_deleted).length === 0 && <p className="text-zinc-400">No documents uploaded</p>}
          </div>
        )}
        {tab === "products" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Product Listings</h3>
              {can("brands.listings.manage") && <Button size="sm" variant="outline" onClick={() => navigate(`/brands/${brandId}/products`)} data-testid="manage-products-btn"><Package className="w-3.5 h-3.5 mr-1" /> Manage Products</Button>}
            </div>
            {listings.length === 0 ? <p className="text-zinc-400">No products listed</p> : listings.map(l => (
              <div key={l._id} className="border rounded-sm p-2 mb-2 flex items-center justify-between">
                <div>
                  <span className="font-medium">{l.product?.product_name || "Unnamed"}</span>
                  <span className="ml-2 text-zinc-500">{(l.skus || []).length} SKUs</span>
                </div>
                <Badge className={l.listing_status === "sent_to_catalogue" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}>{pretty(l.listing_status)}</Badge>
              </div>
            ))}
          </div>
        )}
        {tab === "timeline" && (
          <div className="space-y-2" data-testid="timeline-tab">
            {changeHistory.map(cr => (
              <div key={cr._id} className="border-l-2 border-blue-200 pl-3 py-1">
                <p className="text-zinc-900">Change request ({cr.status}): {cr.reason}</p>
                {Object.entries(cr.changes || {}).map(([k, v]) => (
                  <p key={k} className="text-[10px] text-zinc-500">{k}: {String(v.old ?? "")} → {String(v.new ?? "")}</p>
                ))}
                <p className="text-zinc-400 text-[10px]">{cr.requested_by_name} — {fmtDateTime(cr.requested_at)}</p>
              </div>
            ))}
            {timeline.map(t => (
              <div key={t._id} className="border-l-2 border-zinc-200 pl-3 py-1">
                <p className="text-zinc-900">{t.detail || pretty(t.action)}</p>
                <p className="text-zinc-400 text-[10px]">{t.user_name} — {fmtDateTime(t.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
